import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useBlocker, useLocation, useMatch, useNavigate } from 'react-router-dom';

import { authSelectors } from 'app-components/flux/auth';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

import ErrorBoundary from 'components/ErrorBoundary';
import { CREDIT_SCORE_DRAWER_PATH, CreditScoreDrawerContext } from 'containers/CreditReport/CreditScoreDrawerContext';
import { useAppSelector } from 'hooks/redux';
import retryPromise from 'utils/retryPromise';

const CreditScoreDrawer = lazy(() => retryPromise(() => import('containers/CreditReport/CreditScoreDrawer')));

export const CreditScoreDrawerProvider = ({ children }: { children: React.ReactNode }): React.JSX.Element => {
  const enabled = useAppSelector(
    (state) =>
      featureFlagsSelectors.getFeatureFlag(state, 'navLeftNavRedesignExperiment') === 'Variant A' &&
      Boolean(featureFlagsSelectors.getFeatureFlag(state, 'creditReport')) &&
      !authSelectors.getCurrentDataset(state)?.shared,
  );
  const location = useLocation();
  const navigate = useNavigate();
  const isCreditScoreRoute = useMatch(CREDIT_SCORE_DRAWER_PATH) !== null;
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const unsavedChangesRef = useRef(false);
  const onUnsavedChangesChange = useCallback((dirty: boolean) => {
    unsavedChangesRef.current = dirty;
  }, []);
  const openCreditScoreDrawer = useCallback((options?: { setup?: boolean }) => {
    setShowSetup(Boolean(options?.setup));
    setOpen(true);
    setHasOpened(true);
  }, []);
  const closeCreditScoreDrawer = useCallback(() => {
    unsavedChangesRef.current = false;
    setOpen(false);
    setShowSetup(false);
  }, []);
  const requestSetup = useCallback(() => setShowSetup(true), []);
  const value = useMemo(
    () => (enabled ? { openCreditScoreDrawer, closeCreditScoreDrawer } : null),
    [closeCreditScoreDrawer, enabled, openCreditScoreDrawer],
  );
  const blocker = useBlocker(
    ({ nextLocation }) =>
      enabled &&
      (Boolean(matchPath(CREDIT_SCORE_DRAWER_PATH, nextLocation.pathname)) || (open && unsavedChangesRef.current)),
  );
  const isBlockedCreditScoreRoute =
    blocker.state === 'blocked' && Boolean(matchPath(CREDIT_SCORE_DRAWER_PATH, blocker.location.pathname));
  const restoringDeepLinkRef = useRef(false);

  useEffect(() => {
    if (!isBlockedCreditScoreRoute || blocker.state !== 'blocked') return;
    // Reset the attempted navigation before it can unmount the originating page.
    blocker.reset();
    if (!open) openCreditScoreDrawer();
  }, [blocker, isBlockedCreditScoreRoute, open, openCreditScoreDrawer]);

  useEffect(() => {
    if (!enabled) {
      closeCreditScoreDrawer();
      return;
    }

    if (!isCreditScoreRoute) {
      if (restoringDeepLinkRef.current) {
        restoringDeepLinkRef.current = false;
        return;
      }
      closeCreditScoreDrawer();
      return;
    }

    openCreditScoreDrawer();
    restoringDeepLinkRef.current = true;
    navigate('/', { replace: true });
  }, [closeCreditScoreDrawer, enabled, isCreditScoreRoute, location, navigate, openCreditScoreDrawer]);

  return (
    <CreditScoreDrawerContext.Provider value={value}>
      {children}
      {enabled && hasOpened ? (
        <ErrorBoundary size='xs' feedbackSubject='Credit score drawer failed to load'>
          <Suspense fallback={null}>
            <CreditScoreDrawer
              onClose={closeCreditScoreDrawer}
              onRequestSetup={requestSetup}
              open={open}
              showSetup={showSetup}
              blocker={isBlockedCreditScoreRoute ? undefined : blocker}
              onUnsavedChangesChange={onUnsavedChangesChange}
            />
          </Suspense>
        </ErrorBoundary>
      ) : null}
    </CreditScoreDrawerContext.Provider>
  );
};
